import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Activity } from '../../models/dashboard';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-activities-business-widget',
  templateUrl: './activities-business-widget.component.html',
  styleUrls: ['./activities-business-widget.component.css'],
  standalone: false
})
export class ActivitiesWidgetComponent implements OnInit {
  @Input() activities: Activity[] = [];
  single: any[] = [];
  view: any[];

  // options
  gradient: boolean = false;
  showLegend: boolean = true;
  showLabels: boolean = false;
  isDoughnut: boolean = false;
  legendPosition: string = 'right';

  legendTitle: string;
  shouldShow = new BehaviorSubject(false);

  colorScheme = {
    domain: [
      '#F7941E',
      'var(--color-primary-500)',
      '#329934',
      '#FF4500',
      '#006AB2',
      '#00CED1',
      '#8A2BE2',
      '#FF1493',
      '#FFD700',
      '#FF6347',
      '#FFA07A',
      '#FF00FF'
    ]
  };

  monthNames: string[] = [];

  constructor(private translate: TranslateService, private router: Router) {}

  /**
   * Initializes the widget by loading month names and setting the legend title.
   */
  ngOnInit(): void {
    this.loadMonthNames();
    this.setLegendTitle();
    setTimeout(() => {
      this.shouldShow.next(true);
    }, 500);
  }

  /**
   *
   * @param changes - The new and old values of the data-bound properties.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['activities'] && changes['activities'].currentValue) {
      this.processActivities(changes['activities'].currentValue);
    }
  }

  /**
   * Loads month names from the translation service and updates the activities list.
   */
  async loadMonthNames() {
    const translationKeys = [
      'JANUARY',
      'FEBRUARY',
      'MARCH',
      'APRIL',
      'MAY',
      'JUNE',
      'JULY',
      'AUGUST',
      'SEPTEMBER',
      'OCTOBER',
      'NOVEMBER',
      'DECEMBER'
    ];

    const translations = await this.translate.get(translationKeys).toPromise();
    this.monthNames = translationKeys.map(key => translations[key]);

    if (this.activities) {
      this.processActivities(this.activities);
    }
  }

  /**
   * Sets the legend title from the translation service.
   */
  async setLegendTitle() {
    this.legendTitle = await this.translate.get('MONTH').toPromise();
  }

  /**
   * Processes the activities array to calculate the number of activities per month
   * and sorts them by the end date.
   *
   * @param activities - Array of activity objects to be processed.
   */
  processActivities(activities: Activity[]) {
    if (!activities || this.monthNames.length === 0) {
      this.single = [];
      return;
    }

    const validActivities = activities
      .filter(activity => {
        const endTime = new Date(activity.EndDate).getTime();
        return !Number.isNaN(endTime);
      })
      .sort(
        (a, b) => new Date(a.EndDate).getTime() - new Date(b.EndDate).getTime()
      );

    if (validActivities.length === 0) {
      this.single = [];
      return;
    }

    const activityCounts = validActivities.reduce((acc, activity) => {
      const endDate = new Date(activity.EndDate);
      const month = endDate.getMonth();
      if (!acc[month]) {
        acc[month] = 0;
      }
      acc[month]++;
      return acc;
    }, {} as { [key: number]: number });

    this.single = this.monthNames
      .map((monthName, monthIndex) => ({
        name: monthName,
        value: activityCounts[monthIndex] || 0
      }))
      .filter(month => month.value > 0);
  }

  /**
   * Handles selection event from the widget and navigates to the follow-up activities page.
   *
   * @param event - The event object containing details about the selected item.
   */
  onSelect(event: any) {
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/');
    segments[segments.length - 1] = 'follow-up';
    segments.push('activities');
    const deviationPath = segments.join('/');

    this.router.navigate([deviationPath]);
  }
}
